import { useEffect, useMemo } from 'react';
import { ReactNode } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Link,
  List,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import NavItem from './NavItem';
import CommunityHelpCard from 'src/components/CommunityHelpCard';
import { APP_VERSION } from 'src/constants';
import { getLinks } from 'src/utils/permissions';

type RenderNavItemsProps = {
  items: Item[];
  pathname: string;
  depth?: number;
  isNew?: boolean;
};

type ReduceChildRoutesProps = {
  acc: any[];
  pathname: string;
  item: Item;
  depth: number;
  isNew: boolean;
};

type NavBarProps = {
  onMobileClose: () => void;
  openMobile: boolean;
};

type Item = {
  href?: string;
  icon?: ReactNode;
  info?: ReactNode;
  items?: Item[];
  title: string;
  new?: boolean;
};

function renderNavItems({
  items,
  pathname,
  depth = 0,
  isNew = false,
}: RenderNavItemsProps): ReactNode {
  return (
    <List disablePadding>
      {items.reduce((acc, item) => reduceChildRoutes({ acc, item, pathname, depth, isNew }), [])}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth,
  isNew,
}: ReduceChildRoutesProps): ReactNode[] {
  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <Box key={item.href} style={{ display: 'flex', alignItems: 'center' }}>
        <NavItem
          depth={depth}
          icon={item.icon}
          info={item.info}
          open={Boolean(open)}
          title={item.title}
        >
          {renderNavItems({
            depth: depth + 1,
            pathname,
            items: item.items,
            isNew,
          })}
        </NavItem>

        {item.new && (
          <Typography color="primary" variant="caption" style={{ width: 111 }}>
            New feature!
          </Typography>
        )}
      </Box>
    );
  } else {
    acc.push(
      <Box key={item.href} style={{ display: 'flex', alignItems: 'center' }}>
        <NavItem
          depth={depth}
          href={item.href}
          icon={item.icon}
          info={item.info}
          title={item.title}
          className={'step' + item.title}
        />

        {item.new && (
          <Typography color="primary" variant="caption" style={{ width: 111 }}>
            New feature!
          </Typography>
        )}
      </Box>
    );
  }

  return acc;
}

const NavBar = ({ onMobileClose, openMobile }: NavBarProps) => {
  const { user } = useAuth();
  const navLinks = useMemo(() => getLinks(user), [user]);
  const classes = useStyles();
  const location = useLocation();
  const theme = useTheme();
  const showMenu = useMediaQuery(theme.breakpoints.up(1520));

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        {!showMenu && (
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        )}

        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/app/account">
              <Avatar alt="User" className={classes.avatar} src={user?.avatarUrl} />
            </RouterLink>
          </Box>

          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {user?.firstName} {user?.lastName}
            </Link>

            <Typography variant="body2" color="textSecondary">
              User Type:&nbsp;
              {user?.userType &&
                {
                  T: 'Therapist',
                  CH: 'Coach',
                  C: 'Client',
                }[user.userType]}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <List>
            {renderNavItems({
              items: navLinks,
              pathname: location.pathname,
            })}
          </List>
        </Box>
      </PerfectScrollbar>

      <CommunityHelpCard />

      <Box p={2}>
        <Typography style={{ fontSize: '11px', color: '#546E7A' }}>
          v{APP_VERSION}
          <br />
          © Copyright 2023
          <br />
          Divergence Neuro Technologies Inc.
        </Typography>
      </Box>
    </Box>
  );

  return (
    (showMenu || openMobile) && (
      <>
        <Box>
          <Drawer
            anchor="left"
            classes={{ paper: classes.mobileDrawer }}
            onClose={onMobileClose}
            open={openMobile}
            variant="temporary"
          >
            {content}
          </Drawer>
        </Box>

        <Box>
          <Drawer
            anchor="left"
            classes={{ paper: classes.desktopDrawer }}
            open
            variant="persistent"
          >
            {content}
          </Drawer>
        </Box>
      </>
    )
  );
};

export default NavBar;

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)',
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64,
  },
}));
